// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Shared merits are not a shared win. On a round or a tour, players level on merits are
// separated by handicap: the lower handicap takes the higher position. The key is the
// player's HCP index when one is recorded, otherwise the playing handicap (SHCP) for the
// round. A player with neither sorts behind everyone who has one, and players who cannot
// be separated at all keep the existing name ordering so the list stays stable.

/** Sorts behind every known handicap. */
export const UNKNOWN_HANDICAP = Number.POSITIVE_INFINITY;

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * The handicap tied merits are broken on: HCP index if the player has one, else SHCP.
 */
export const resolveTieBreakHandicap = (handicap?: unknown, shcp?: unknown): number =>
  toFiniteNumber(handicap) ?? toFiniteNumber(shcp) ?? UNKNOWN_HANDICAP;

/**
 * Orders two tie-break handicaps, lowest first. Two unknown handicaps are equal — plain
 * subtraction would yield NaN there and leave the comparator undefined.
 */
export const compareHandicaps = (a: number, b: number): number => {
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

export type StandingsRow = {
  /** Merit total the standing is built on; higher is better. */
  merits: number;
  /** Tie-break handicap from {@link resolveTieBreakHandicap}. */
  handicap: number;
  /** Last resort, so equal merits and equal handicaps still order predictably. */
  name: string;
};

/** Comparator for overall standings: merits desc, then handicap asc, then name. */
export const compareStandings = (a: StandingsRow, b: StandingsRow): number =>
  b.merits - a.merits || compareHandicaps(a.handicap, b.handicap) || a.name.localeCompare(b.name);
